import { useState, useEffect, useCallback } from "react";
import "./App.css";
import { AuthProvider, useAuth } from "contexts/AuthContext";
import Login from "pages/Login";
import ProjectSidebar from "components/ProjectSidebar";
import Leaderboard from "components/Leaderboard";
import MarketersView from "components/MarketersView";
import EntriesTable from "components/EntriesTable";
import AdminPanel from "components/AdminPanel";
import SyncPanel from "components/SyncPanel";
import UploadPanel from "components/UploadPanel";
import ProjectSettings from "components/ProjectSettings";
import FilteredPostsTab from "components/FilteredPostsTab";
import ContentGenerator from "components/ContentGenerator";
import ChecklistTab from "components/ChecklistTab";
import KeywordsTab from "components/KeywordsTab";
import DailyReportTab from "components/DailyReportTab";
import HealthDashboard from "components/HealthDashboard";
import SettingsPage from "components/SettingsPage";
import BridgeControl from "components/BridgeControl";
import ScheduleTab from "components/ScheduleTab";
import {
  MessageSquare, Check, X, RefreshCw, Loader2, Download, Zap, LogOut, Lock, User
} from "lucide-react";

function Dashboard() {
  const { api, profile, isAdmin, logout } = useAuth();
  const isClient = profile?.role === "client";
  const [activeTab, setActiveTab] = useState("entries");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [marketerStats, setMarketerStats] = useState([]);
  const [marketersList, setMarketersList] = useState([]);
  const [checkStatus, setCheckStatus] = useState(null);
  const [checkAllRunning, setCheckAllRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ password: "", name: "" });
  const [profileLoading, setProfileLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchProjects = useCallback(async () => {
    try { const res = await api.get("/projects"); setProjects(res.data); } catch {}
  }, [api]);

  const fetchStats = useCallback(async () => {
    try {
      const params = selectedProject ? { project_id: selectedProject } : {};
      const res = await api.get("/stats/overview", { params });
      setStats(res.data);
    } catch {}
  }, [api, selectedProject]);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const params = selectedProject ? { project_id: selectedProject } : {};
      const res = await api.get("/stats/leaderboard", { params });
      setLeaderboard(res.data);
    } catch {}
  }, [api, selectedProject]);

  const fetchMarketerStats = useCallback(async () => {
    try {
      const params = selectedProject ? { project_id: selectedProject } : {};
      const res = await api.get("/stats/marketers", { params });
      setMarketerStats(res.data);
    } catch {}
  }, [api, selectedProject]);

  const fetchMarketersList = useCallback(async () => {
    try {
      const params = selectedProject ? { project_id: selectedProject } : {};
      const res = await api.get("/stats/marketers-list", { params });
      setMarketersList(res.data);
    } catch {}
  }, [api, selectedProject]);

  const fetchCheckStatus = useCallback(async () => {
    try {
      const res = await api.get("/sync/check-status");
      setCheckStatus(res.data);
      setCheckAllRunning(res.data.is_running);
    } catch {}
  }, [api]);

  const refreshAll = useCallback(() => {
    fetchStats();
    fetchLeaderboard();
    fetchMarketerStats();
    fetchMarketersList();
    fetchCheckStatus();
    setRefreshKey((k) => k + 1);
  }, [fetchStats, fetchLeaderboard, fetchMarketerStats, fetchMarketersList, fetchCheckStatus]);

  useEffect(() => { fetchProjects(); fetchCheckStatus(); }, [fetchProjects, fetchCheckStatus]);
  useEffect(() => { if (!isClient) refreshAll(); }, [refreshAll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isClient && projects.length > 0 && !selectedProject) setSelectedProject(projects[0].id);
  }, [isClient, projects, selectedProject]);

  useEffect(() => {
    if (isClient && !selectedProject) return;
    refreshAll();
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!checkAllRunning) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get("/sync/check-status");
        if (!res.data.is_running) {
          setCheckAllRunning(false);
          showToast("Check All completed!");
          refreshAll();
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [checkAllRunning, api, refreshAll]);

  const handleCheckAll = async () => {
    try {
      const res = await api.post("/sync/check-all?limit=500");
      if (res.data.status === "busy") { showToast("Already running", "error"); return; }
      setCheckAllRunning(true);
      showToast(`Checking ${res.data.checking} entries...`);
    } catch { showToast("Failed", "error"); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = selectedProject ? `?project_id=${selectedProject}` : "";
      const res = await api.get(`/entries/export${params}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "reddit_tracker_export.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Downloaded!");
    } catch { showToast("Export failed", "error"); } finally { setExporting(false); }
  };

  const handleProfileUpdate = async () => {
    if (!profileForm.password && !profileForm.name) return;
    setProfileLoading(true);
    try {
      const body = {};
      if (profileForm.password) body.password = profileForm.password;
      if (profileForm.name) body.name = profileForm.name;
      await api.put("/auth/update-credentials", body);
      showToast("Profile updated!");
      setShowProfile(false);
      setProfileForm({ password: "", name: "" });
    } catch (err) {
      showToast(err.response?.data?.detail || "Update failed", "error");
    } finally { setProfileLoading(false); }
  };

  const handleCreateProject = async (data) => {
    try {
      await api.post("/projects", data);
      showToast(`Project "${data.display_name}" created!`);
      fetchProjects();
    } catch (err) { showToast(err.response?.data?.detail || "Failed", "error"); }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm(`Delete project "${id}"?`)) return;
    try {
      await api.delete(`/projects/${id}`);
      if (selectedProject === id) setSelectedProject("");
      showToast("Project deleted");
      fetchProjects();
    } catch { showToast("Failed", "error"); }
  };

  const projectName = selectedProject
    ? projects.find((p) => p.id === selectedProject)?.display_name || selectedProject
    : "All Projects";

  return (
    <div className="app-layout" data-testid="dashboard">
      <ProjectSidebar
        projects={projects} selectedProject={selectedProject}
        onSelectProject={setSelectedProject} onCreateProject={handleCreateProject}
        onDeleteProject={handleDeleteProject} collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} isAdmin={isAdmin}
        isClient={isClient} activeTab={activeTab} onTabChange={setActiveTab}
      />
      <div className="main-content">
        {/* Compact top bar */}
        <header className="topbar" data-testid="topbar">
          <div className="topbar-left">
            <span className="topbar-context" data-testid="topbar-context">{projectName}</span>
            {profile && (
              <span className="topbar-role" data-testid="user-role-badge">
                {profile.role === "admin" ? "Admin" : profile.role === "client" ? "Client" : profile.name}
              </span>
            )}
          </div>
          <div className="topbar-actions">
            {!isClient && (
              <button className="topbar-btn topbar-btn-check" onClick={handleCheckAll} disabled={checkAllRunning} data-testid="check-all-btn">
                {checkAllRunning ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                {checkAllRunning ? "Checking..." : `Check (${checkStatus?.unknown_entries || 0})`}
              </button>
            )}
            {!isClient && (
              <button className="topbar-btn" onClick={handleExport} disabled={exporting} data-testid="export-btn">
                {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              </button>
            )}
            <button className="topbar-btn" onClick={refreshAll} data-testid="refresh-btn"><RefreshCw size={14} /></button>
            <button className="topbar-btn" onClick={() => setShowProfile(true)} data-testid="profile-btn"><User size={14} /></button>
            <button className="topbar-btn" onClick={logout} data-testid="logout-btn"><LogOut size={14} /></button>
          </div>
        </header>

        {checkAllRunning && (
          <div className="check-all-banner" data-testid="check-all-banner">
            <Loader2 size={14} className="animate-spin" /><span>Checking entries on Reddit...</span>
          </div>
        )}

        {/* Compact inline stats */}
        {stats && (
          <div className="stats-bar" data-testid="stats-grid">
            <div className="stat-pill total" data-testid="total-entries"><MessageSquare size={12} /> <strong>{stats.total_entries}</strong> total</div>
            <div className="stat-pill live"><Check size={12} /> <strong>{stats.live}</strong> live</div>
            <div className="stat-pill deleted"><X size={12} /> <strong>{stats.deleted}</strong> deleted</div>
            <div className="stat-pill unknown"><strong>{stats.unknown}</strong> unknown</div>
            {(stats.duplicate > 0) && <div className="stat-pill duplicate" data-testid="duplicate-count"><strong>{stats.duplicate}</strong> duplicate</div>}
            {(stats.imported > 0) && <div className="stat-pill imported"><strong>{stats.imported}</strong> to assign</div>}
            {(stats.assigned > 0) && <div className="stat-pill assigned"><strong>{stats.assigned}</strong> assigned</div>}
            <div className="stat-pill"><strong>{stats.unique_marketers}</strong> marketers</div>
          </div>
        )}

        {/* Main content area */}
        <div className="content-area" data-testid="content-area">
          {activeTab === "leaderboard" && <Leaderboard data={leaderboard} />}
          {activeTab === "marketers" && <MarketersView data={marketerStats} />}
          {activeTab === "entries" && (
            <EntriesTable projects={projects} selectedProject={selectedProject}
              marketers={marketersList} onRefresh={refreshAll} refreshKey={refreshKey} />
          )}
          {activeTab === "admin" && isAdmin && <AdminPanel projects={projects} refreshKey={refreshKey} />}
          {activeTab === "settings" && isAdmin && (
            <ProjectSettings
              project={selectedProject ? projects.find((p) => p.id === selectedProject) : null}
              onUpdated={fetchProjects}
            />
          )}
          {activeTab === "sync" && <SyncPanel />}
          {activeTab === "upload" && <UploadPanel onRefresh={refreshAll} projects={projects} selectedProject={selectedProject} />}
          {activeTab === "filtered" && <FilteredPostsTab selectedProject={selectedProject} />}
          {activeTab === "content" && <ContentGenerator projects={projects} selectedProject={selectedProject} />}
          {activeTab === "checklist" && <ChecklistTab selectedProject={selectedProject} />}
          {activeTab === "report" && <DailyReportTab selectedProject={selectedProject} />}
          {activeTab === "keywords" && <KeywordsTab selectedProject={selectedProject} />}
          {activeTab === "health" && isAdmin && <HealthDashboard />}
          {activeTab === "bridge" && isAdmin && <BridgeControl />}
          {activeTab === "schedule" && isAdmin && <ScheduleTab />}
          {activeTab === "app-settings" && isAdmin && <SettingsPage />}
        </div>

        {/* Profile modal */}
        {showProfile && (
          <div className="modal-overlay" onClick={() => setShowProfile(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }} data-testid="profile-modal">
              <div className="modal-header">
                <h3><User size={16} /> Profile Settings</h3>
                <button className="modal-close" onClick={() => setShowProfile(false)}><X size={18} /></button>
              </div>
              <div className="modal-body">
                <div style={{ marginBottom: 16 }}>
                  <label className="field-label">Current Email</label>
                  <div className="field-value">{profile?.email}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="field-label">Display Name</label>
                  <input className="filter-input" placeholder={profile?.name || "Your name"}
                    value={profileForm.name} onChange={(e) => setProfileForm((p) => ({ ...p, name: e.target.value }))} data-testid="profile-name" />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label className="field-label">New Password</label>
                  <input className="filter-input" type="password" placeholder="Leave blank to keep current"
                    value={profileForm.password} onChange={(e) => setProfileForm((p) => ({ ...p, password: e.target.value }))} data-testid="profile-password" />
                </div>
                <button className="btn btn-primary" style={{ width: "100%" }} onClick={handleProfileUpdate}
                  disabled={profileLoading || (!profileForm.password && !profileForm.name)} data-testid="profile-save-btn">
                  {profileLoading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                  Update Profile
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div className={`toast ${toast.type}`} data-testid="toast">
            {toast.type === "success" ? <Check size={18} color="#10b981" /> : <X size={18} color="#ef4444" />}
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { profile, loading } = useAuth();
  if (loading) {
    return (
      <div className="login-page">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }
  if (!profile) return <Login />;
  return <Dashboard />;
}

export default App;
